import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useLang } from '../../../../util/lang';
import { useDispatch } from 'react-redux';
import instance from '../../../../axios-default';
import { handleTriggerRefetch, setIsEditedValueInPatientInfo } from '../../RequestComment/RequestCommentSlice';
import { easySnackbar } from '../../../../store/Noti/noti.actions';
import { Box, InputAdornment, TextField, Typography, makeStyles } from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import EditIcon from '@material-ui/icons/Edit';
import style from '../../clinicalSimulation.module.css';
import { COMMENT_TYPE, resetDraftComment, setDraftComment, } from '../../../../store/ClinicalSimulation/clinicalSimulation';
import useRequestCommentContext from '../../hooks/useRequestCommentContext';
import EditInfo, { EDIT_INFO_TYPE } from '../EditInfo/EditInfo';
import useAuth from '../../../../hooks/useAuth';
import { ROLES_FOR_COMMENT } from '../../hooks/useRequestCommentCommon';

const useStyles = makeStyles(() => ({
    noBorder: {
        '& fieldset': { border: 'none !important', },
    },
    readOnlyPadding: {
        padding: '0px !important',
        '& .MuiOutlinedInput-inputMultiline': { padding: '0px !important', },
    },
    editableNoPadding: {
        padding: '0px !important',
        '& .MuiOutlinedInput-inputMultiline': { padding: '8px !important', },
    },
    commentInputLabel: {
        fontSize: '12px !important',
        fontWeight: '600',
        '& *': { fontSize: '10px !important', fontWeight: '600', fontFamily: 'Montserrat !important', }
    }
}));

const InputOptions = ({ handleSubmit, isEdit, handleCmBoxCommentEditing }) => {
    return (
        <InputAdornment style={{ position: 'absolute', bottom: 16, right: 8 }} position="end">
            {!isEdit ? (
                <Box onClick={handleCmBoxCommentEditing}>
                    <EditIcon style={{ cursor: 'pointer', fontSize: '18px' }} className={style.messageButton} />
                </Box>
            ) : (
                <Box
                    onClick={() => {
                        handleSubmit();
                    }}
                >
                    <SendIcon style={{ cursor: 'pointer', fontSize: '18px' }} className={style.messageButton} />
                </Box>
            )}
        </InputAdornment>
    );
};

const CommentInput = ({
    label,
    defaultValue,
    readOnly = false,
    caseId,
    planId,
    updatedDate,
    isEditing,
    updatedBy = '',
    created_date = '',
}) => {
    const get = useLang();
    const dispatch = useDispatch();
    const classes = useStyles();

    const auth = useAuth();
    const { role } = auth?.user || {};

    const [value, setValue] = useState(defaultValue);
    const [initialDefaultValue, setInitialDefaultValue] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const { setLoading, handleCmBoxCommentEditing } = useRequestCommentContext();

    const readOnlyFlag = readOnly || !isEditing;

    const handleOnChange = useCallback(
        (e) => {
            setValue(e.target.value);
            if (planId) {
                dispatch(
                    setDraftComment({
                        currentPlanId: planId,
                        value: e.target.value,
                        type: COMMENT_TYPE.DESIGNER_COMMENT,
                    })
                );
            }
        },
        [planId]
    );

    const handleSubmit = useCallback(async () => {
        if (/^\s*$/.test(value)) {
            setErrorMessage('Input should not be empty or contain only spaces');
        } else {
            setErrorMessage('');

            const data = {
                value: value,
                plan_id: planId,
            };

            try {
                setLoading(true);
                defaultValue === '' && setInitialDefaultValue(value);

                const result = await instance.post(`case/treatment_plan_comment/${caseId}`, data);

                if (defaultValue === '') {
                    setInitialDefaultValue(result.data.results);
                }
                dispatch(setIsEditedValueInPatientInfo(true));
                if (result.data.success) {
                    dispatch(easySnackbar(get('@TreatmentPlanHistory.updatedDesignerComment'), 'success'));
                    dispatch(handleTriggerRefetch());
                    dispatch(resetDraftComment());
                }
            } catch (e) {
                dispatch(easySnackbar('Cannot update, try to refresh the page', 'error'));
            } finally {
                setLoading(false);
            }
        }
    }, [value]);

    const placeholder = useMemo(() => {
        if (!ROLES_FOR_COMMENT.includes(role)) return '';
        const langKey = planId ? 'comment.inputPlaceholder' : 'comment.waitPlanUploadPlaceholder';
        return get(`@TreatmentPlanHistory.${langKey}`);
    }, [planId, role]);

    useEffect(() => {
        setValue(defaultValue);
    }, [defaultValue]);

    return (
        <>
            <Box>
                <Box style={{ display: "flex", justifyContent: "space-between", alignContent: "center", padding: "4px 0", alignItems: "center", gap: "8px" }}>
                    <Box>
                        <Typography className={classes.commentInputLabel}>
                            {label}
                        </Typography>
                    </Box>
                    <Box>
                        <EditInfo type={EDIT_INFO_TYPE.COMMENT} updatedDate={updatedDate} created_date={created_date} updatedBy={updatedBy} />
                    </Box>
                </Box>
                <TextField
                    placeholder={placeholder}
                    multiline
                    minRows={4}
                    style={{ width: '100%' }}
                    variant={'outlined'}
                    InputProps={{
                        readOnly: readOnlyFlag,
                        classes: {
                            root: `${readOnly ? `${classes.readOnlyPadding} ${classes.noBorder}` : classes.editableNoPadding}`
                        },
                        startAdornment: !readOnly && (
                            <InputOptions
                                handleSubmit={handleSubmit}
                                defaultValue={initialDefaultValue !== '' ? initialDefaultValue : defaultValue}
                                isEdit={isEditing}
                                handleCmBoxCommentEditing={() => {
                                    if (handleCmBoxCommentEditing && planId) {
                                        handleCmBoxCommentEditing(planId);
                                    }
                                }}
                            />
                        )
                    }}
                    inputProps={{ style: { fontSize: '14px', } }}
                    InputLabelProps={{ shrink: true, }}
                    value={value ?? ''}
                    onChange={handleOnChange}
                />
            </Box>
            <Box color="red" marginY={0.5} fontSize={10.5} fontWeight="600" textAlign="justify">
                {errorMessage}
            </Box>
        </>
    );
};

export default memo(CommentInput);